/**
 * iter 51 — Reusable Org Admin content body.
 *
 * Renders the Users / Locations / Departments management UI for a given org.
 * Used in two places:
 *   • Wrapped in a <Drawer> by OrgAdminDrawer.jsx (Organizations page row click)
 *   • Rendered inline by Settings.jsx ("My Organization" tab) when the
 *     user is editing their own org from the settings surface.
 *
 * `embedded=true` removes the top breadcrumb (since the parent surface
 * already shows the section context).
 */
import { useEffect, useState, useCallback } from "react";
import {
  Buildings, UsersThree, MapPin, Buildings as BuildingsAlt,
  Star, Plus, Lock, PencilSimple, CaretRight, ShieldStar,
  ClockClockwise, Trash, EnvelopeOpen, PaperPlaneTilt,
  EnvelopeSimpleOpen, Megaphone,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import UserSubDrawer from "./UserManagerDrawer";
import LocationSubDrawer from "./LocationManagerDrawer";
import DepartmentSubDrawer from "./DepartmentManagerDrawer";
import AnnouncementSubDrawer from "./AnnouncementManagerDrawer";
import AnnouncementSchedule from "./AnnouncementSchedule";

const SECTION_KEYS = ["pending", "users", "locations", "departments", "roles", "announcements"];

export default function OrgAdminContent({ orgId, orgName, embedded = false, active = true }) {
  const [perms, setPerms] = useState(null);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [announcements, setAnnouncements] = useState([]);   // iter 73.10
  const [collabCode, setCollabCode] = useState("");   // iter 55
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [subDrawer, setSubDrawer] = useState(null); // { type, entity|null }
  const [expanded, setExpanded] = useState(() => new Set(SECTION_KEYS));

  // iter 70 — inline role create/rename state.
  const [newRoleName, setNewRoleName] = useState("");
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingRoleName, setEditingRoleName] = useState("");
  const [roleBusy, setRoleBusy] = useState(false);

  const loadAll = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setErr("");
    try {
      const [p, u, l, d, r, inv, ann] = await Promise.all([
        api.get(`/org-admin/${orgId}/permissions`).then((r) => r.data),
        api.get(`/org-admin/${orgId}/users`).then((r) => r.data),
        api.get(`/org-admin/${orgId}/locations`).then((r) => r.data),
        api.get(`/org-admin/${orgId}/departments`).then((r) => r.data),
        api.get(`/org-admin/${orgId}/roles`).then((r) => r.data).catch(() => []),
        api.get(`/org-admin/${orgId}/invitations`).then((r) => r.data).catch(() => []),
        api.get(`/announcements/org/${orgId}`).then((r) => r.data).catch(() => []),
      ]);
      setPerms(p); setUsers(u); setLocations(l); setDepartments(d); setRoles(r); setInvitations(inv); setAnnouncements(ann);
      // Collaboration code is own-org only — gracefully skip on 403 partner views.
      if (p?.own_org) {
        try {
          const cr = await api.get(`/org-admin/${orgId}/collaboration-code`);
          setCollabCode(cr.data.code || "");
        } catch (_) { /* non-fatal */ }
      } else {
        setCollabCode("");
      }
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // ---------- Role CRUD ----------
  const createRole = async () => {
    if (!newRoleName.trim()) return;
    setRoleBusy(true); setErr("");
    try {
      await api.post(`/org-admin/${orgId}/roles`, { name: newRoleName.trim() });
      setNewRoleName("");
      await loadAll();
      toast.success("Role added.");
    } catch (e) { setErr(formatErr(e)); }
    finally { setRoleBusy(false); }
  };
  const startEditRole = (r) => {
    setEditingRoleId(r.id);
    setEditingRoleName(r.name);
  };
  const saveRoleRename = async () => {
    if (!editingRoleId || !editingRoleName.trim()) return;
    setRoleBusy(true); setErr("");
    try {
      await api.patch(`/org-admin/${orgId}/roles/${editingRoleId}`, { name: editingRoleName.trim() });
      setEditingRoleId(null); setEditingRoleName("");
      await loadAll();
      toast.success("Role renamed.");
    } catch (e) { setErr(formatErr(e)); }
    finally { setRoleBusy(false); }
  };
  const deleteRole = async (r) => {
    if (!confirm(`Delete the "${r.name}" role?`)) return;
    setRoleBusy(true); setErr("");
    try {
      await api.delete(`/org-admin/${orgId}/roles/${r.id}`);
      await loadAll();
      toast.success(`"${r.name}" deleted.`);
    } catch (e) { setErr(formatErr(e)); }
    finally { setRoleBusy(false); }
  };

  // ---------- Invitations ----------
  const revokeInvite = async (inv) => {
    if (!confirm(`Revoke the pending invitation for ${inv.email}?`)) return;
    setErr("");
    try {
      await api.delete(`/org-admin/${orgId}/invitations/${inv.id}`);
      await loadAll();
      toast.success(`Invitation for ${inv.email} revoked.`);
    } catch (e) { setErr(formatErr(e)); }
  };
  const resendInvite = async (inv) => {
    setErr("");
    try {
      const r = await api.post(`/org-admin/${orgId}/invitations/${inv.id}/resend`);
      await loadAll();
      if (r.data?.sent_via) {
        toast.success(`Invitation re-sent to ${inv.email} from ${r.data.sent_via}.`);
      } else {
        toast.success(
          `Invitation token refreshed for ${inv.email}. (No connected Gmail — please share the link manually.)`,
        );
      }
    } catch (e) { setErr(formatErr(e)); }
  };

  useEffect(() => { if (active && orgId) loadAll(); }, [active, orgId, loadAll]);

  const toggleSection = (k) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const onSubDrawerClose = (savedAny = false) => {
    setSubDrawer(null);
    if (savedAny) loadAll();
  };

  // iter 73.9 — One-click "Confirm Email" for stuck users. Fires
  // `PUT /api/org-admin/{org_id}/users/{user_id}/confirm-email` which
  // idempotently sets email_confirm=true on the Supabase auth row.
  // Toasts success either way (or "already confirmed" if the user was
  // never actually stuck). Wrapped in a per-row busy state so double-
  // clicks don't hammer Supabase.
  const [confirmingId, setConfirmingId] = useState(null);
  const confirmEmail = async (uRec, ev) => {
    ev?.stopPropagation();
    if (confirmingId) return;
    setConfirmingId(uRec.id);
    try {
      const { data } = await api.put(`/org-admin/${orgId}/users/${uRec.id}/confirm-email`);
      if (data?.already_confirmed) {
        toast.info(`${uRec.email} was already confirmed.`);
      } else {
        toast.success(`Confirmed ${uRec.email}. They can now sign in.`);
      }
      // iter 73.10 — refresh so the "PENDING" chip flips off immediately.
      await loadAll();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setConfirmingId(null);
    }
  };

  const locCount = (uid) => users.find((u) => u.id === uid)?.location_ids?.length || 0;

  return (
    <div data-testid="org-admin-content">
      {!embedded && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3" data-testid="org-admin-breadcrumb">
          <Buildings size={12} /> <span className="font-medium">{orgName}</span>
        </div>
      )}

      {perms && !perms.own_org && (
        <div
          className={`surface p-2.5 mb-3 text-xs flex items-center gap-2 ${perms.can_edit_org ? "" : "border-yellow-300 bg-yellow-50 dark:bg-yellow-950/40"}`}
          data-testid="org-admin-perm-banner"
        >
          {perms.can_edit_org ? (
            <>
              <PencilSimple size={14} className="text-green-600" />
              <span><strong>Editable</strong> — you are this customer&apos;s only partner.</span>
            </>
          ) : (
            <>
              <Lock size={14} className="text-yellow-700" />
              <span>
                <strong>Read-only</strong> — this customer has <strong>{perms.partner_count}</strong> partner orgs.
                Org details must be managed by the customer themselves. You can still manage their users, locations, and departments.
              </span>
            </>
          )}
        </div>
      )}

      {loading && <div className="text-xs text-muted-foreground py-6 text-center">Loading…</div>}
      {err && <div className="surface p-3 text-xs text-destructive mb-3" data-testid="org-admin-error">{err}</div>}

      {!loading && perms && (
        <>
          {/* Collaboration code (own-org only) */}
          {perms.own_org && collabCode && (
            <div className="surface p-2.5 mb-3 flex items-center justify-between gap-3" data-testid="org-admin-collab-code">
              <div className="flex items-center gap-2">
                <span className="label-xs">COLLABORATION CODE</span>
                <code className="mono text-sm font-bold tracking-widest">{collabCode}</code>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => { navigator.clipboard?.writeText(collabCode); }}
                title="Copy code"
                data-testid="org-admin-collab-copy"
              >COPY</button>
            </div>
          )}

          {invitations.length > 0 && (
            <>
              <SectionHeader
                title="Pending invitations"
                count={invitations.length}
                icon={EnvelopeOpen}
                expanded={expanded.has("pending")}
                onToggle={() => toggleSection("pending")}
                action={null}
                testid="org-admin-pending"
              />
              {expanded.has("pending") && (
                <div className="space-y-1.5 mb-4" data-testid="org-admin-pending-list">
                  {invitations.map((inv) => (
                    <div
                      key={inv.id}
                      className="surface px-3 py-2 flex items-center gap-2"
                      data-testid={`org-admin-invite-row-${inv.id}`}
                    >
                      <ClockClockwise size={12} className="text-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{inv.email}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {inv.role_name ? `Invited as ${inv.role_name}` : "No role assigned"}
                          {inv.expires_at && <> · Expires {new Date(inv.expires_at).toLocaleDateString()}</>}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => resendInvite(inv)}
                        title="Resend invitation (rotates token, refreshes expiry to 7 days)"
                        data-testid={`org-admin-invite-resend-${inv.id}`}
                      >
                        <PaperPlaneTilt size={12} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm text-destructive"
                        onClick={() => revokeInvite(inv)}
                        title="Revoke invitation"
                        data-testid={`org-admin-invite-revoke-${inv.id}`}
                      >
                        <Trash size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <SectionHeader
            title="Users"
            count={users.length}
            icon={UsersThree}
            expanded={expanded.has("users")}
            onToggle={() => toggleSection("users")}
            action={perms.can_manage_users ? (
              <button className="btn btn-primary btn-sm" onClick={() => setSubDrawer({ type: "user", entity: null })} data-testid="org-admin-add-user-btn" type="button">
                <Plus size={12} /> ADD USER
              </button>
            ) : null}
            testid="org-admin-users"
          />
          {expanded.has("users") && (
            <div className="space-y-1.5 mb-4" data-testid="org-admin-users-list">
              {users.length === 0 && (
                <div className="text-xs text-muted-foreground italic py-3 text-center">
                  No users yet. The first user you create will be the Super Admin.
                </div>
              )}
              {users.map((u) => {
                const roleNames = u.role_names && u.role_names.length > 0
                  ? u.role_names
                  : (u.role ? [u.role] : []);
                const userDeptNames = (u.department_ids || [])
                  .map((did) => departments.find((d) => d.id === did)?.name)
                  .filter(Boolean);
                return (
                <RowCard key={u.id} onClick={() => setSubDrawer({ type: "user", entity: u })} testid={`org-admin-user-row-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                      {roleNames.includes("super_admin") && (
                        <Star size={11} weight="fill" className="text-yellow-500 flex-shrink-0" title="Super Admin" />
                      )}
                      {u.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate flex flex-wrap items-center gap-1">
                      <span>{u.email}</span>
                      {/* iter 73.10 — surface pending Supabase email
                          confirmation as an amber chip so admins can spot
                          stuck users without clicking through every row.
                          Populated by the /api/webhooks/supabase-auth hook
                          + one-time backfill; NULL means "still pending". */}
                      {u.email_confirmed_at === null && (
                        <span
                          className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[9px] mono uppercase tracking-wide border border-amber-500/40"
                          title="This user hasn't confirmed their email address yet — they can't sign in. Click the envelope icon to force-confirm them."
                          data-testid={`user-row-email-pending-${u.id}`}
                        >
                          Pending
                        </span>
                      )}
                      {roleNames.map((rn) => (
                        <span key={rn} className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[9px] mono uppercase tracking-wide" data-testid={`user-row-role-${rn}`}>
                          {rn}
                        </span>
                      ))}
                      {userDeptNames.map((dn) => (
                        <span key={dn} className="px-1.5 py-0.5 rounded bg-muted text-foreground text-[9px] mono uppercase tracking-wide">
                          {dn}
                        </span>
                      ))}
                      {locCount(u.id) > 0 && (
                        <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[9px] mono uppercase tracking-wide">
                          {locCount(u.id)} loc
                        </span>
                      )}
                    </div>
                  </div>
                  {/* iter 73.9 — one-click support action to unstick a user
                      whose Supabase confirmation email never arrived. Idempotent
                      (server returns already_confirmed=true on a re-click), and
                      hidden if perms don't include can_manage_users. */}
                  {perms.can_manage_users && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm flex-shrink-0"
                      onClick={(ev) => confirmEmail(u, ev)}
                      disabled={confirmingId === u.id}
                      title="Force-confirm this user's email address (unblocks login if the Supabase confirmation email never arrived)"
                      data-testid={`org-admin-user-confirm-email-${u.id}`}
                    >
                      <EnvelopeSimpleOpen
                        size={12}
                        className={confirmingId === u.id ? "animate-pulse" : ""}
                      />
                    </button>
                  )}
                  <CaretRight size={12} className="text-muted-foreground flex-shrink-0" />
                </RowCard>
                );
              })}
            </div>
          )}

          <SectionHeader
            title="Locations"
            count={locations.length}
            icon={MapPin}
            expanded={expanded.has("locations")}
            onToggle={() => toggleSection("locations")}
            action={perms.can_manage_locations ? (
              <button className="btn btn-primary btn-sm" onClick={() => setSubDrawer({ type: "location", entity: null })} data-testid="org-admin-add-location-btn" type="button">
                <Plus size={12} /> ADD LOCATION
              </button>
            ) : null}
            testid="org-admin-locations"
          />
          {expanded.has("locations") && (
            <div className="space-y-1.5 mb-4" data-testid="org-admin-locations-list">
              {locations.length === 0 && (
                <div className="text-xs text-muted-foreground italic py-3 text-center">
                  No locations yet. The first one you create will be the Main Location.
                </div>
              )}
              {locations.map((l) => (
                <RowCard key={l.id} onClick={() => setSubDrawer({ type: "location", entity: l })} testid={`org-admin-loc-row-${l.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                      {l.is_main_location && (
                        <Star size={11} weight="fill" className="text-primary flex-shrink-0" title="Main Location" />
                      )}
                      {l.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {[l.address1, l.city, l.state, l.postal_code].filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                  <CaretRight size={12} className="text-muted-foreground flex-shrink-0" />
                </RowCard>
              ))}
            </div>
          )}

          <SectionHeader
            title="Departments"
            count={departments.length}
            icon={BuildingsAlt}
            expanded={expanded.has("departments")}
            onToggle={() => toggleSection("departments")}
            action={perms.can_manage_departments ? (
              <button className="btn btn-primary btn-sm" onClick={() => setSubDrawer({ type: "department", entity: null })} data-testid="org-admin-add-department-btn" type="button">
                <Plus size={12} /> ADD DEPT
              </button>
            ) : null}
            testid="org-admin-departments"
          />
          {expanded.has("departments") && (
            <div className="space-y-1.5 mb-4" data-testid="org-admin-departments-list">
              {departments.length === 0 && (
                <div className="text-xs text-muted-foreground italic py-3 text-center">
                  No departments yet.
                </div>
              )}
              {departments.map((d) => {
                const inDept = users.filter((u) => (u.department_ids || []).includes(d.id)).length;
                const managerNames = (d.manager_user_ids || [])
                  .map((mid) => users.find((u) => u.id === mid)?.name)
                  .filter(Boolean);
                return (
                  <RowCard key={d.id} onClick={() => setSubDrawer({ type: "department", entity: d })} testid={`org-admin-dept-row-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{d.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {inDept} user{inDept === 1 ? "" : "s"}
                        {managerNames.length > 0 && <> · led by {managerNames.join(", ")}</>}
                        {d.description && <> · {d.description}</>}
                      </div>
                    </div>
                    <CaretRight size={12} className="text-muted-foreground flex-shrink-0" />
                  </RowCard>
                );
              })}
            </div>
          )}

          <SectionHeader
            title="Roles"
            count={roles.length}
            icon={ShieldStar}
            expanded={expanded.has("roles")}
            onToggle={() => toggleSection("roles")}
            action={null}
            testid="org-admin-roles"
          />
          {expanded.has("roles") && (
            <div className="space-y-1.5 mb-4" data-testid="org-admin-roles-list">
              {roles.length === 0 && (
                <div className="text-xs text-muted-foreground italic py-3 text-center">
                  No roles yet.
                </div>
              )}
              {roles.map((r) => {
                const userCount = users.filter((u) => (u.role_ids || []).includes(r.id)).length;
                const isEditing = editingRoleId === r.id;
                return (
                  <div
                    key={r.id}
                    className="surface px-3 py-2 flex items-center gap-2"
                    data-testid={`org-admin-role-row-${r.id}`}
                  >
                    <ShieldStar size={11} className={r.is_system ? "text-yellow-500" : "text-muted-foreground"} weight={r.is_system ? "fill" : "regular"} />
                    {isEditing ? (
                      <input
                        className="input flex-1"
                        value={editingRoleName}
                        autoFocus
                        onChange={(e) => setEditingRoleName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRoleRename(); if (e.key === "Escape") setEditingRoleId(null); }}
                        data-testid={`org-admin-role-rename-input-${r.id}`}
                      />
                    ) : (
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {userCount} user{userCount === 1 ? "" : "s"}
                        </div>
                      </div>
                    )}
                    <span className={`text-[9px] mono uppercase tracking-widest px-1.5 py-0.5 rounded ${r.is_system ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {r.is_system ? "SYSTEM" : "CUSTOM"}
                    </span>
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={saveRoleRename}
                          disabled={roleBusy || !editingRoleName.trim()}
                          data-testid={`org-admin-role-save-${r.id}`}
                        >SAVE</button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => { setEditingRoleId(null); setEditingRoleName(""); }}
                          data-testid={`org-admin-role-cancel-${r.id}`}
                        >X</button>
                      </>
                    ) : !r.is_system && (
                      <>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => startEditRole(r)}
                          title="Rename"
                          data-testid={`org-admin-role-edit-${r.id}`}
                        ><PencilSimple size={12} /></button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-destructive"
                          onClick={() => deleteRole(r)}
                          title="Delete role"
                          data-testid={`org-admin-role-delete-${r.id}`}
                        ><Trash size={12} /></button>
                      </>
                    )}
                  </div>
                );
              })}
              <div className="flex items-center gap-2 pt-2" data-testid="org-admin-role-add">
                <input
                  className="input flex-1"
                  placeholder="Add a custom role (e.g. 'Field Supervisor')"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") createRole(); }}
                  data-testid="org-admin-role-new-input"
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={createRole}
                  disabled={roleBusy || !newRoleName.trim()}
                  data-testid="org-admin-role-add-btn"
                ><Plus size={12} /> ADD</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* iter 73.10 — Announcements section (admin-only surface).       */}
      {perms?.own_org && perms?.can_manage_users && (
        <>
          <SectionHeader
            title="Announcements"
            count={announcements.length}
            icon={Megaphone}
            expanded={expanded.has("announcements")}
            onToggle={() => toggleSection("announcements")}
            action={
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setSubDrawer({ type: "announcement", entity: null })}
                data-testid="org-admin-announcement-new-btn"
              ><Plus size={12} /> NEW</button>
            }
            testid="org-admin-announcements"
          />
          {expanded.has("announcements") && (
            <div className="space-y-1.5 mb-4" data-testid="org-admin-announcements-list">
              {announcements.length === 0 && (
                <div className="text-xs text-muted-foreground italic py-3 text-center">
                  No announcements yet. Publish one to show a banner at the top of every internal member's screen.
                </div>
              )}
              {/* iter 73.10+ — Month schedule Gantt (only when there ARE announcements). */}
              <AnnouncementSchedule
                announcements={announcements}
                onSelect={(a) => setSubDrawer({ type: "announcement", entity: a })}
              />
              {announcements.map((a) => {
                const priorityColor = {
                  info:     "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40",
                  warning:  "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40",
                  critical: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40",
                }[a.priority] || "bg-muted";
                const anyTarget = (a.targeting?.departments?.length || 0)
                                + (a.targeting?.roles?.length || 0)
                                + (a.targeting?.users?.length || 0);
                const preview = (a.content_html || "").replace(/<[^>]*>/g, "").slice(0, 140);
                return (
                  <RowCard
                    key={a.id}
                    onClick={() => setSubDrawer({ type: "announcement", entity: a })}
                    testid={`org-admin-announcement-row-${a.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-1.5 py-0.5 rounded text-[9px] mono uppercase tracking-wide border flex-shrink-0 ${priorityColor}`}
                          data-testid={`org-admin-announcement-priority-${a.id}`}
                        >{a.priority}</span>
                        <span className="text-sm truncate flex-1">{preview}</span>
                        {/* iter 73.10+ — read-receipt counters */}
                        <span
                          className="text-[10px] text-muted-foreground mono flex-shrink-0"
                          title={`${a.seen_count || 0} users have seen this banner; ${a.dismissed_count || 0} have dismissed it.`}
                          data-testid={`org-admin-announcement-counts-${a.id}`}
                        >{a.seen_count || 0} seen · {a.dismissed_count || 0} dismissed</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {anyTarget === 0
                          ? "Target: everyone"
                          : `Target: ${a.targeting?.departments?.length || 0} depts · ${a.targeting?.roles?.length || 0} roles · ${a.targeting?.users?.length || 0} users`}
                        {a.ends_at && ` · Ends ${new Date(a.ends_at).toLocaleString()}`}
                      </div>
                    </div>
                    <CaretRight size={12} className="text-muted-foreground flex-shrink-0" />
                  </RowCard>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Unified manager drawers (mode-driven: create/edit). */}
      <UserSubDrawer
        open={subDrawer?.type === "user"}
        onClose={() => onSubDrawerClose(false)}
        onSuccess={() => onSubDrawerClose(true)}
        mode={subDrawer?.entity ? "edit" : "create"}
        initialData={subDrawer?.entity || null}
        orgId={orgId}
        orgName={orgName}
        locations={locations}
        departments={departments}
        roles={roles}
        canDelete={users.length > 1 || subDrawer?.entity?.role !== "super_admin"}
      />
      <LocationSubDrawer
        open={subDrawer?.type === "location"}
        onClose={() => onSubDrawerClose(false)}
        onSuccess={() => onSubDrawerClose(true)}
        mode={subDrawer?.entity ? "edit" : "create"}
        initialData={subDrawer?.entity || null}
        orgId={orgId}
        orgName={orgName}
        locationKind="office"
        isOnlyLocation={locations.length === 1}
      />
      <DepartmentSubDrawer
        open={subDrawer?.type === "department"}
        onClose={() => onSubDrawerClose(false)}
        onSuccess={() => onSubDrawerClose(true)}
        mode={subDrawer?.entity ? "edit" : "create"}
        initialData={subDrawer?.entity || null}
        orgId={orgId}
        orgName={orgName}
        users={users}
      />
      <AnnouncementSubDrawer
        open={subDrawer?.type === "announcement"}
        onClose={() => onSubDrawerClose(false)}
        onSuccess={() => onSubDrawerClose(true)}
        mode={subDrawer?.entity ? "edit" : "create"}
        initialData={subDrawer?.entity || null}
        orgId={orgId}
        departments={departments}
        roles={roles}
        users={users}
      />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Local UI helpers
// ----------------------------------------------------------------------------

function SectionHeader({ title, count, icon: Icon, expanded, onToggle, action, testid }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border mb-2" data-testid={`${testid}-header`}>
      <button type="button" onClick={onToggle} className="flex items-center gap-2 text-left flex-1 group">
        <CaretRight size={12} className={`text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`} />
        <Icon size={14} className="text-muted-foreground" />
        <span className="font-semibold uppercase tracking-wide text-xs group-hover:text-foreground">{title}</span>
        <span className="mono text-[10px] text-muted-foreground bg-muted/40 rounded px-1.5 py-0.5">{count}</span>
      </button>
      <div onClick={(e) => e.stopPropagation()}>{action}</div>
    </div>
  );
}

function RowCard({ children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full surface p-2.5 hover:bg-muted/40 transition-colors flex items-center gap-2 text-left"
      data-testid={testid}
    >
      {children}
    </button>
  );
}
